import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { 
  Paper, Box, Container, Tooltip, Grid, Typography, Card, 
  CardContent, Stack,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Input from "@mui/material/Input";
import Button from "@mui/material/Button";
import launcherIcon from "../assets/logo-no-bg.svg";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import CloseIcon from "@mui/icons-material/Close";

import { get, put } from "../../shared/http/httpService";
import { setRenderChat } from "../../shared/redux/clientSlice";

const DEFAULT_CONFIG = {
  url: "",
  size: "Small",
  chatTitle: "",
  chatTitleImg: "",
  chatColor: "#4E8CFF",
  launcherCloseIcon: true,
};

const IconPreview = ({ showCloseIcon, imageUrl, selectedSize, imageSizes }) => (
  <Box sx={{ marginTop: 2, display: "flex", justifyContent: "center" }}>
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: imageSizes[selectedSize] || imageSizes["Small"],
        height: imageSizes[selectedSize] || imageSizes["Small"],
        borderRadius: "50%",
        backgroundColor: "#4E8CFF",
        position: "relative",
      }}
    >
      {!showCloseIcon ? (
        <img
          src={(imageUrl && imageUrl !== "") ? imageUrl : launcherIcon}
          alt="Client Image"
          style={{
            width: "inherit",
            height: "inherit",
          }}
        />
      ) : (
        <CloseIcon
          style={{
            color: "white",
            width: "80%",
            height: "80%",
          }}
        />
      )}
    </div>
  </Box>
);

const ButtonTab = () => {
  const [imageUrl, setImageUrl] = useState("");
  const { t } = useTranslation("clients");
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [selectedSize, setSelectedSize] = useState("Small");
  const [chatTitle, setChatTitle] = useState("");
  const [fullConfig, setFullConfig] = useState({...DEFAULT_CONFIG});
  const [launcherCloseIcon, setLauncherCloseIcon] = useState(true);
  const imageSizes = {
    Small: "60px",
    Medium: "75px",
    Large: "90px",
  };
  const client = useSelector((state) => state.client);

  let auth = useSelector((state) => state.auth);
  let [userPermissions, setUserPermissions] = React.useState(false);

  const getUserPermisions = () => {
    let user = JSON.parse(atob(auth.token.split(".")[1]));
    let hasPermissions = user.permissions.some(
      (permission) =>
        permission.name === "file" && permission.fullAccess === true,
    );
    setUserPermissions(hasPermissions);
  };

  useEffect(() => {
    getChatCConfig();
    getUserPermisions();
  }, [client.name]);

  const getChatCConfig = () => {
    let body = { clientName: client.name };

    get("/file/ChatCConfig/", body)
      .then(async (res) => {
        const config = { ...DEFAULT_CONFIG, ...res };
        setImageUrl(config.url);
        setSelectedSize(config.size || "Small");
        setChatTitle(config.chatTitle || "");
        setLauncherCloseIcon(config.launcherCloseIcon !== undefined ? config.launcherCloseIcon : true);
        setFullConfig(config);
      })
      .catch((error) => {
        if (error.response && error.response.status === 400) {
          setImageUrl("");
          setSelectedSize("Small");
          setChatTitle("");
          setLauncherCloseIcon(true);
          setFullConfig({...DEFAULT_CONFIG, clientName: client.name});
        }
      });
  };

  const updateFile = () => {
    const body = {
      ...fullConfig,
      clientName: client.name,
      url: imageUrl,
      size: selectedSize,
      chatTitle: chatTitle,
      launcherCloseIcon: launcherCloseIcon,
    };

    put("/file/ChatCConfig/", body)
      .then(async () => {
        enqueueSnackbar(t("personalization:fileUpdated"), {
          variant: "success",
        });
        setFullConfig(body);
        dispatch(setRenderChat());
      })
      .catch(() => {
        enqueueSnackbar(t("personalization:errorUploadingFile"), {
          variant: "error",
        });
      });
  };

  const handleImageUrlChange = (event) => {
    setImageUrl(event.target.value);
  };

  const handleSwitchChange = (event) => {
    setLauncherCloseIcon(!event.target.checked);
  };

  return (
    <Container>
      <Paper elevation={3} sx={{ padding: "20px", marginY: 2, textAlign: "center" }}>
        <h1 style={{ marginTop: "20px", textAlign: "center" }}>
          {t("personalization:avatarCustomization")}
        </h1>
        <FormControl style={{ width: "70%", marginBottom: "2rem" }}>
          <Box>
            <InputLabel 
              id="image-url-label"
              sx={{ 
                position: "absolute",
                transform: "translate(0, 0) scale(1)",
                transformOrigin: "top left",
                transition: "transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms",
                "&.Mui-focused, &.MuiFormLabel-filled": {
                  transform: "translate(0, -20px) scale(0.75)", 
                },
              }}
            >
              {t("personalization:image")}
            </InputLabel>
            <Tooltip
              title={userPermissions ? "" : t("noPermissionTooltip")}>
              <span>
                <Input
                  labelId="image-url-label"
                  value={imageUrl || ""}
                  onChange={handleImageUrlChange}
                  style={{ width: "100%" }}
                  disabled={!userPermissions}
                />
              </span>
            </Tooltip>
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={1}>
            <span
              style={{
                color: "gray",
                width: "100%",
                fontSize: "0.8rem",
                textAlign: "left",
              }}>
              {t("personalization:recomendation")}
            </span>
          </Box>
        </FormControl>
        {/* Two column layout */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Left Column */}
          <Grid item xs={12} md={6}>
            <Card elevation={2} sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("personalization:sizeConfiguration")}
                </Typography>
                <Tooltip title={userPermissions ? "" : t("noPermissionTooltip")}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Select
                      labelId="size-select-label"
                      id="size-select"
                      value={selectedSize || "Small"}
                      onChange={(event) => setSelectedSize(event.target.value)}
                      disabled={!userPermissions}>
                      <MenuItem value={"Small"}>{t("personalization:SizeSmall")}</MenuItem>
                      <MenuItem value={"Medium"}>{t("personalization:SizeMedium")}</MenuItem>
                      <MenuItem value={"Large"}>{t("personalization:SizeLarge")}</MenuItem>
                    </Select>
                  </FormControl>
                </Tooltip>                
                <IconPreview 
                  showCloseIcon={false} 
                  imageUrl={imageUrl} 
                  selectedSize={selectedSize} 
                  imageSizes={imageSizes} 
                />
              </CardContent>
            </Card>
          </Grid>
          {/* Right Column */}
          <Grid item xs={12} md={6}>
            <Card elevation={2} sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("personalization:CloseIconConfiguration")}
                </Typography>
                <Tooltip title={userPermissions ? "" : t("noPermissionTooltip")}>
                  <FormControlLabel
                    control={
                      <Switch
                        id="launcher-close-icon-switch"
                        checked={!launcherCloseIcon}
                        onChange={handleSwitchChange}
                        disabled={!userPermissions}
                        color="primary"
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>
                          {launcherCloseIcon? t("personalization:showImage"): t("personalization:showCloseIcon")}
                        </Typography>
                      </Stack>
                    }
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ height: "15px" }}></Box>  
                  <IconPreview 
                    showCloseIcon={launcherCloseIcon} 
                    imageUrl={imageUrl} 
                    selectedSize={selectedSize} 
                    imageSizes={imageSizes} 
                  />
                </Tooltip>
              </CardContent>
            </Card>
          </Grid>
        </Grid>    
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Tooltip title={userPermissions ? "" : t("noPermissionTooltip")}>
            <span>
              <Button
                variant="contained"
                color="primary"
                disabled={!userPermissions}
                onClick={updateFile}
                size="large"
              >
                {t("save")}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>
    </Container>
  );
};

export default ButtonTab;